import { useCallback, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { renamePage } from '../../services/PageOperations';
import { sectionIdForNode } from '../v2-common/ContentNodeExtensions';
import { useNavigateToSection } from '../v2-hooks/useUrlHash';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';

/** Presentation state for the "Edit page title" modal on the Contents tab. */
export interface EditPageButtonViewModel {
  readonly isModalVisible: boolean;
  readonly title: TiptapOutputType;
  /** The optional navigation/table-of-contents short title. */
  readonly shortTitle: TiptapOutputType;
  /** True when the Save button should be enabled. */
  readonly isSaveEnabled: boolean;
  /** True while the new title is being persisted. */
  readonly isSaving: boolean;
  /** Inline error to display after a failed save attempt. Undefined when no error. */
  readonly saveErrorMessage: string | undefined;
  readonly openModal: () => void;
  readonly dismissModal: () => void;
  /** Persist the new title and close the modal. No-op when invalid or already saving. */
  readonly confirmSave: () => void;
  readonly updateTitle: (newTitle: TiptapOutputType, isValidationPassed: boolean) => void;
  readonly updateShortTitle: (newShortTitle: TiptapOutputType, isValidationPassed: boolean) => void;
}

/**
 * Drives the "Edit page title" modal. Saves the new title via
 * ContentEditorService.stageEdit + flush, regenerating the page's href
 * from the new title to match legacy behavior.
 */
export function useEditPageButtonViewModel(currentPage: ContentNode): EditPageButtonViewModel {
  const service = useContentEditorService();
  const navigateToSection = useNavigateToSection();
  const entityConverter = useEntityConverter();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [title, setTitle] = useState<TiptapOutputType>(currentPage.title ?? '');
  const [shortTitle, setShortTitle] = useState<TiptapOutputType>(currentPage.shortTitle ?? '');
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isShortTitleValid, setIsShortTitleValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined);

  // When the user clicks the pencil, seed the editor with the current title.
  // Assume the existing title is valid (it was accepted at creation time).
  const openModal = useCallback(() => {
    setTitle(currentPage.title ?? '');
    setShortTitle(currentPage.shortTitle ?? '');
    setIsTitleValid(true);
    setIsShortTitleValid(true);
    setSaveErrorMessage(undefined);
    setIsModalVisible(true);
  }, [currentPage]);

  // When the modal is dismissed, reset all form state.
  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    setTitle('');
    setShortTitle('');
    setIsTitleValid(false);
    setIsShortTitleValid(true);
    setIsSaving(false);
    setSaveErrorMessage(undefined);
  }, []);

  // When the title input changes, store the new value and let the input
  // tell us whether it passes validation. Length bounds are enforced by
  // the input itself, not the view model.
  const updateTitle = useCallback((newTitle: TiptapOutputType, isValidationPassed: boolean) => {
    setTitle(newTitle);
    setIsTitleValid(isValidationPassed);
  }, []);

  // When the short title input changes, store the new value and its validity.
  // The short title is optional, so an empty value is valid.
  const updateShortTitle = useCallback(
    (newShortTitle: TiptapOutputType, isValidationPassed: boolean) => {
      setShortTitle(newShortTitle);
      setIsShortTitleValid(isValidationPassed);
    },
    [],
  );

  // When the user confirms, save the new title/short title and close the modal.
  const confirmSave = useCallback(() => {
    if (!isTitleValid || !isShortTitleValid || isSaving) return;
    const content = service.content;
    if (!content) return;
    setIsSaving(true);
    setSaveErrorMessage(undefined);
    void (async () => {
      try {
        // Persist a blank short title as undefined so it clears the field, matching legacy behavior.
        const shortTitleToSave =
          entityConverter(shortTitle).trim().length > 0 ? shortTitle : undefined;
        const renamed = renamePage(content, currentPage.id, title, shortTitleToSave);
        if (!renamed) return;

        // The URL hash references the page by its old href; update it so the
        // current page resolution still finds this page after the rename.
        navigateToSection(sectionIdForNode(renamed.page));

        service.stageEdit(renamed.content);
        await service.flush();
        dismissModal();
      } catch (error) {
        // Only handle the expected save failure here. Anything else (null
        // reference, programming bug, etc.) is re-thrown so the app's error
        // boundary can catch and report it.
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save edited page title to working copy');
        setSaveErrorMessage(
          'There was an error saving the title. Please wait a few seconds and retry.',
        );
      } finally {
        setIsSaving(false);
      }
    })();
  }, [
    isTitleValid,
    isShortTitleValid,
    isSaving,
    service,
    title,
    shortTitle,
    entityConverter,
    currentPage,
    navigateToSection,
    dismissModal,
  ]);

  return {
    isModalVisible,
    title,
    shortTitle,
    isSaveEnabled: isTitleValid && isShortTitleValid && !isSaving,
    isSaving,
    saveErrorMessage,
    openModal,
    dismissModal,
    confirmSave,
    updateTitle,
    updateShortTitle,
  };
}
